import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { purchaseOrderAttachmentsApi } from '#/api/srm/purchaseOrder/attachments';
import { createPurchaseOrder } from '#/api/srm/purchaseOrder/create';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { updatePurchaseOrder } from '#/api/srm/purchaseOrder/update';
import { fetchSupplierSelectOptions } from '#/api/srm/supplier/fetchSelectOptions';
import { fetchCurrencySelectOptions } from '#/api/system/currency';
import { fetchPaymentTermSelectOptions } from '#/api/system/paymentTerm';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { cn } from '#/lib/utils';
import {
  DocumentAttachmentsCard,
  uploadPendingAttachmentsAfterCreate,
} from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import PurchaseOrderLinesEditor from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderLinesEditor';
import PurchaseOrderMainInfoCard from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderMainInfoCard';
import PurchaseOrderStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderStatusBadge';
import {
  getPurchaseOrderApiErrorMessage,
  isPurchaseOrderVersionConflict,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  buildPurchaseOrderWritePayload,
  computePurchaseOrderAmount,
  mapPurchaseOrderDetailToFormValues,
  mergeLookupOption,
} from '#/modules/srmMgmt/purchaseOrder/helper/formSerialize';
import {
  parsePurchaseOrderCreatePrefillParams,
  prefillPurchaseOrderFromSearchParams,
} from '#/modules/srmMgmt/purchaseOrder/helper/prefillPurchaseOrderFromSearchParams';
import { applyDocumentCurrencyFieldError } from '#/utils/documentCurrencyError';
import { formatDisplayAmount } from '#/utils/formatNumber';
import { canEditPurchaseOrderDraft } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';
import {
  PURCHASE_ORDER_FIELD_LABELS,
  PURCHASE_ORDER_ROUNDING_MODE_OPTIONS,
  PURCHASE_ORDER_TAX_TYPE_OPTIONS,
  purchaseOrderFormSchema,
} from '#/modules/srmMgmt/purchaseOrder/schemas/purchaseOrderSchema';
import { ROUNDING_MODE } from '#/utils/roundingMode';
import {
  lookupQueryKey,
  purchaseOrderDetailQueryKey,
  purchaseOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const DEFAULT_VALUES = {
  purchaseOrderDate: '',
  supplierId: '',
  currencyId: '',
  paymentTermId: '',
  receiptWarehouseId: '',
  exchangeRate: 1,
  taxType: 'TAX_EXCLUDED',
  taxRate: 5,
  roundingMode: ROUNDING_MODE.HALF_UP,
  issueReason: '',
  remark: '',
  lines: [],
};

const CreateEditPurchaseOrderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updatedAtRef = useRef(null);
  const prefillStartedRef = useRef(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
    setCustomMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(id),
    queryFn: () => fetchPurchaseOrderDetail(id),
    enabled: Boolean(id),
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const supplierOptionsQuery = useQuery({
    queryKey: lookupQueryKey('supplierSelect'),
    queryFn: fetchSupplierSelectOptions,
  });

  const currencyOptionsQuery = useQuery({
    queryKey: lookupQueryKey('currencySelect'),
    queryFn: fetchCurrencySelectOptions,
  });

  const paymentTermOptionsQuery = useQuery({
    queryKey: lookupQueryKey('paymentTermSelect'),
    queryFn: fetchPaymentTermSelectOptions,
  });

  async function createPurchaseOrderWithAttachments(payload) {
    const result = await createPurchaseOrder(payload);
    const createdId = result?.data?.id;
    if (createdId) {
      await uploadPendingAttachmentsAfterCreate(
        purchaseOrderAttachmentsApi,
        String(createdId),
        pendingFiles,
      );
    }
    return result;
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createPurchaseOrderWithAttachments,
    updateFn: updatePurchaseOrder,
    buildPayload: (values) => buildPurchaseOrderWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: purchaseOrderListQueryKeyPrefix,
    detailQueryKey: purchaseOrderDetailQueryKey,
    getDetailPath: ROUTES.purchaseOrderDetail,
    afterUpdateNavigateTo: id ? ROUTES.purchaseOrderDetail(id) : null,
    getErrorMessage: (error) => getPurchaseOrderApiErrorMessage(error, '儲存失敗'),
    onError: (error) => applyDocumentCurrencyFieldError(error, setError),
    isVersionConflict: isPurchaseOrderVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(purchaseOrderFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const watchedTaxType = useWatch({ control, name: 'taxType' });
  const watchedTaxRate = useWatch({ control, name: 'taxRate' });

  const amountPreview = useMemo(
    () => computePurchaseOrderAmount(watchedLines, watchedTaxType, watchedTaxRate),
    [watchedLines, watchedTaxType, watchedTaxRate],
  );

  const detail = detailQuery.data?.data;

  useEffect(() => {
    if (!isEdit || !detail) {
      return;
    }

    if (!canEditPurchaseOrderDraft(detail.status)) {
      toast.warning('僅草稿可編輯，已返回詳情');
      navigate(ROUTES.purchaseOrderDetail(id), { replace: true });
    }
  }, [isEdit, detail, id, navigate]);

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapPurchaseOrderDetailToFormValues(detail));
  }, [detail, reset, resetValidationSummary]);

  useEffect(() => {
    if (isEdit || prefillStartedRef.current) {
      return undefined;
    }

    const { hasPrefill } = parsePurchaseOrderCreatePrefillParams(searchParams);
    if (!hasPrefill) {
      return undefined;
    }

    prefillStartedRef.current = true;
    let cancelled = false;

    prefillPurchaseOrderFromSearchParams({
      searchParams,
      getValues,
      setValue,
      navigate,
      setCustomMessages,
      markSubmitAttempted,
    }).catch(() => {
      if (!cancelled) {
        navigate(ROUTES.purchaseOrderCreate, { replace: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    getValues,
    isEdit,
    markSubmitAttempted,
    navigate,
    searchParams,
    setCustomMessages,
    setValue,
  ]);

  const currencyOptions = useMemo(
    () => mergeLookupOption(currencyOptionsQuery.data ?? [], detail?.currency),
    [currencyOptionsQuery.data, detail?.currency],
  );

  const paymentTermOptions = useMemo(
    () => mergeLookupOption(paymentTermOptionsQuery.data ?? [], detail?.paymentTerm),
    [paymentTermOptionsQuery.data, detail?.paymentTerm],
  );

  const warehouseOptions = useMemo(
    () => mergeLookupOption(
      warehouseOptionsQuery.data ?? [],
      detail?.receiptWarehouse ?? detail?.defaultWarehouse,
    ),
    [warehouseOptionsQuery.data, detail?.receiptWarehouse, detail?.defaultWarehouse],
  );

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯物料採購單' : '新增物料採購單';
  const headerStatus = isEdit ? (detail?.status || 'DRAFT') : 'DRAFT';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditPurchaseOrderDraft(detail.status);

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              <PurchaseOrderStatusBadge status={headerStatus} size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.purchaseOrder}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getPurchaseOrderApiErrorMessage(detailQuery.error, '找不到物料採購單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <PurchaseOrderStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.purchaseOrderDetail(id)}>返回詳情</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <div className="space-y-4">
              {isEdit && detail ? (
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-600">原始單據資訊</p>
                  <PurchaseOrderMainInfoCard detail={detail} />
                </div>
              ) : null}

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={PURCHASE_ORDER_FIELD_LABELS}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                  <section className="pb-6">
                    <div
                      className={cn(
                        'grid w-full grid-cols-1 divide-y divide-gray-200',
                        '@4xl/sheet:grid-cols-2',
                        '@4xl/sheet:divide-x @4xl/sheet:divide-y-0',
                      )}
                    >
                      <div
                        className={cn(
                          '@container/po-doc min-w-0 space-y-3 pb-6',
                          '@4xl/sheet:pr-6 @4xl/sheet:pb-0',
                        )}
                      >
                        <div className="form-block">
                          <h2 className="form-block__title">單據資訊</h2>
                        </div>
                        <div
                          className={cn(
                            'grid grid-cols-1 gap-3',
                            '@sm/po-doc:grid-cols-2',
                          )}
                        >
                          <FloatingDateInput
                            id="purchaseOrderDate"
                            label="採購日"
                            control={control}
                            errors={errors}
                            placeholder="西元年/月/日"
                          />
                          <FloatingSelect
                            id="supplierId"
                            label="供應商"
                            options={supplierOptionsQuery.data ?? []}
                            control={control}
                            errors={errors}
                            isDisabled={supplierOptionsQuery.isLoading}
                            noOptionsMessage="無可用供應商"
                          />
                          <FloatingSelect
                            id="receiptWarehouseId"
                            label="預設倉"
                            options={warehouseOptions}
                            control={control}
                            errors={errors}
                            isDisabled={warehouseOptionsQuery.isLoading}
                            noOptionsMessage="無可用倉別"
                          />
                          <div
                            className="hidden @sm/po-doc:block"
                            aria-hidden="true"
                          />
                          <FloatingTextarea
                            id="issueReason"
                            label="採購事由"
                            containerClassName={cn(
                              'col-span-full',
                              '@2xl/po-doc:col-span-1',
                            )}
                            register={register}
                            errors={errors}
                          />
                          <FloatingTextarea
                            id="remark"
                            label="備註"
                            containerClassName={cn(
                              'col-span-full',
                              '@2xl/po-doc:col-span-1',
                            )}
                            register={register}
                            errors={errors}
                          />
                        </div>
                      </div>

                      <div
                        className={cn(
                          '@container/po-money min-w-0 space-y-3 pt-6',
                          '@4xl/sheet:pl-6 @4xl/sheet:pt-0',
                        )}
                      >
                        <div className="form-block">
                          <h2 className="form-block__title">金額與稅率</h2>
                        </div>
                        <div
                          className={cn(
                            'grid grid-cols-1 gap-3',
                            '@sm/po-money:grid-cols-2',
                          )}
                        >
                          <FloatingSelect
                            id="currencyId"
                            label="幣別"
                            options={currencyOptions}
                            control={control}
                            errors={errors}
                            isDisabled={currencyOptionsQuery.isLoading}
                            noOptionsMessage="無可用幣別"
                          />
                          <FloatingSelect
                            id="paymentTermId"
                            label="付款條件"
                            options={paymentTermOptions}
                            control={control}
                            errors={errors}
                            isDisabled={paymentTermOptionsQuery.isLoading}
                            noOptionsMessage="無可用付款條件"
                          />
                          <FloatingInput
                            id="exchangeRate"
                            label="匯率（1 單據幣＝TWD）"
                            type="number"
                            readOnly
                            min={0}
                            step="any"
                            register={register}
                            errors={errors}
                          />
                          <FloatingSelect
                            id="roundingMode"
                            label="進位法"
                            options={PURCHASE_ORDER_ROUNDING_MODE_OPTIONS}
                            control={control}
                            errors={errors}
                            isClearable={false}
                          />
                          <FloatingSelect
                            id="taxType"
                            label="稅別"
                            options={PURCHASE_ORDER_TAX_TYPE_OPTIONS}
                            control={control}
                            errors={errors}
                          />
                          <FloatingInput
                            id="taxRate"
                            label="稅率（%，後端）"
                            type="number"
                            readOnly
                            min={0}
                            step="any"
                            register={register}
                            errors={errors}
                          />
                          <div
                            className={cn(
                              'col-span-full min-w-0 rounded-md',
                              'border border-gray-200 bg-gray-50 px-4 py-3',
                            )}
                          >
                            <p className="text-xs text-gray-500">總金額（預覽）</p>
                            <p className="mt-1 text-xl font-bold tabular-nums text-gray-900 underline">
                              {formatDisplayAmount(amountPreview.totalAmount)}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {`未稅 ${formatDisplayAmount(amountPreview.subtotalAmount)}`
                                + ` ／ 稅額 ${formatDisplayAmount(amountPreview.taxAmount)}`}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              實際金額由後端依進位法計算
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <PurchaseOrderLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    getValues={getValues}
                    setValue={setValue}
                  />

                  {isEdit && id ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      businessId={String(id)}
                      ownerKey="purchaseOrder"
                      api={purchaseOrderAttachmentsApi}
                      policy={ATTACHMENT_POLICY_STANDARD}
                    />
                  ) : !isEdit ? (
                    <DocumentAttachmentsCard
                      variant="section"
                      className="pb-6"
                      ownerKey="purchaseOrder"
                      api={purchaseOrderAttachmentsApi}
                      policy={ATTACHMENT_POLICY_STANDARD}
                      pendingFiles={pendingFiles}
                      onPendingFilesChange={setPendingFiles}
                    />
                  ) : null}
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.purchaseOrder}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditPurchaseOrderPage;
